<template>
    <div class="InputDropdown">
        <button
            :id="`js-dropdown-input-${inputId}`"
            :aria-expanded="isOptionsOpen"
            aria-haspopup="listbox"
            :class="{
                'InputDropdown-button--withIcon': icon || image ,
                'InputDropdown-button--notEmpty': !!value ,
                'InputDropdown-button--inactive': disabled
            }"
            class="InputDropdown-button d-flex align-items-center justify-content-between"
            type="button"
            :disabled="disabled"
            @click="toggleOptions"
        >
            <span
                v-if="icon"
                :class="icon"
                class="InputDropdown-customIcon"
            />
            <span
                v-if="image"
                class="InputDropdown-imageContainer"
            >
                <img
                    :src="image"
                    :alt="withSlotItem ? items[value] : getItems"
                    class="InputDropdown-image"
                >
            </span>
            <div
                :class="{ 'InputDropdown-label--withIcon': icon || image }"
                class="InputDropdown-label"
            >
                {{ label }}
                <span
                    v-if="isRequired"
                    class="InputDropdown-required"
                >*</span>
            </div>
            <slot
                v-if="withSlotItem"
                :item="items[value]"
                name="currentOption"
            />
            <template v-else>
                <div class="InputDropdown-value">
                    {{ getItems }}
                </div>
            </template>
            <span class="InputDropdown-iconContainer pl-3">
                <span
                    :class="isOptionsOpen ? 'InputDropdown-icon--opened' : ''"
                    class="InputDropdown-icon common-chevron-bottom"
                />
            </span>
        </button>
        <div
            class="InputDropdown-optionsContainer"
        >
            <input-options
                :backgroundColorSelected="backgroundColorSelected"
                :colorSelected="colorSelected"
                :items="items"
                :isOptionsOpen="getIsOptionsOpen"
                :value="value"
                maxHeight="170px"
                @input="optionChanged"
            />
        </div>
    </div>
</template>

<script>
import InputOptions from '@/components/InputOptions.vue';

export default {
    components: {
        InputOptions,
    },
    props: {
        backgroundColorSelected: {
            type: String,
            default: '#FCE6F2'
        },
        colorSelected: {
            type: String,
            default: '#E6007E'
        },
        icon: {
            type: String,
            default: ''
        },
        image: {
            type: String,
            default: ''
        },
        isRequired: {
            type: Boolean,
            default: false
        },
        items: {
            type: [Array, Object],
            required: true,
        },
        label: {
            type: String,
            required: true
        },
        value: {
            type: [Number, String, Array],
            required: true,
        },
        withSlotItem: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            inputId: null,
            isOptionsOpen: false
        }
    },
    computed: {
        getIsOptionsOpen () {
            return this.isOptionsOpen
        },
        getItems () {
            if (this.isValueArray) {
                let items = ''

                this.value.forEach((item, index) => {
                    items += index === this.value.length - 1 ? this.items[item] : `${this.items[item]}, `
                })

                return items
            } else {
                return this.items[this.value]
            }
        },
        isValueArray () {
            return Array.isArray(this.value)
        }
    },
    mounted () {
        this.inputId = this._uid
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('keyup', this.handleKeyEscape)
    },
    destroyed () {
        document.removeEventListener('click', this.handleClickOutside)
        window.addEventListener('keyup', this.handleKeyEscape)
    },
    methods: {
        closeDropdown () {
            this.isOptionsOpen = false
        },
        handleClickOutside (event) {
            event.stopPropagation()

            if ((!event.target.classList.value.includes('InputOptions')
                    || !event.target.parentElement.classList.value.includes('InputOptions'))
                && (!event.target.classList.value.includes('InputDropdown')
                    || !event.target.parentElement.classList.value.includes('InputDropdown')
                    || (event.target.id && event.target.id !== `js-dropdown-input-${this.inputId}`))
                && this.isOptionsOpen) {
                setTimeout(() => {
                    this.closeDropdown()
                }, 0)
            }
        },
        handleKeyEscape (event) {
            if (event.which === 27) {
                this.closeDropdown()
            }
        },
        optionChanged (selectedOption) {
            if (!this.isValueArray) {
                this.closeDropdown()
            }

            this.$emit('input', selectedOption)
        },
        toggleOptions () {
            this.isOptionsOpen = !this.isOptionsOpen
        }
    }
}
</script>

<style lang='scss' scoped>
.InputDropdown {
    &-button {
        position: relative;
        width: 100%;
        padding: $padding-medium $padding-small $padding-xsmall $padding-small;
        border: $border-input;
        border-radius: 30px;
        background-color: $color-white;
        font-size: $font-medium;
        line-height: $font-large;
        text-align: left;
        color: $color-f2m-purple;
        letter-spacing: $letterSpacing-small;
        transition: box-shadow .3s ease-in-out;


        &--withIcon {
            padding-left: 60px;
        }

        &--notEmpty {
            box-shadow: $boxShadow-ultraLight;

            .InputDropdown-label {
                top: 6px;
                font-size: $font-xsmall;
                line-height: $font-xsmall;
            }
        }

        &--inactive {
            cursor: default;
            opacity: .4;
        }
    }

    &-customIcon {
        position: absolute;
        top: $padding-small;
        left: $padding-large;
        font-size: $font-icon;
        color: $color-f2m-purple;
        pointer-events: none;
    }

    &-imageContainer {
        @include verticalCenter;

        justify-content: center;
        position: absolute;
        top: $padding-small;
        left: $padding-large;
        pointer-events: none;
    }

    &-image {
        width: 20px;
        height: 20px;
        border-radius: $borderRadius-medium;
    }

    &-label {
        @include handleOverflowTextContent;

        display: block;
        position: absolute;
        top: $margin-small;
        left: $margin-small;
        width: calc(100% - $padding-small * 2);
        font-size: $font-medium;
        line-height: $font-large;
        pointer-events: none;
        color: $color-f2m-purple--inactive;
        letter-spacing: $letterSpacing-small;
        transition: top .3s ease-in-out, font-size .3s ease-in-out, line-height .3s ease-in-out;

        &--withIcon {
            left: 60px;
            width: calc(100% - $padding-small * 2 - 60px);
        }
    }

    &-iconContainer {
        pointer-events: none;
    }

    &-icon {
        display: block;
        font-size: $font-icon;
        color: $color-f2m-pink;
        transition: transform .3s ease-in-out;

        &--opened {
            transform: rotate(180deg);
        }
    }

    &-optionsContainer {
        position: relative;
    }

    &-required {
        color: $color-error;
    }
}
</style>
